import {
  Component,
  computed,
  inject,
  model,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  input,
  output,
} from '@angular/core';

import {Dialog} from 'primeng/dialog';
import {Checkbox} from 'primeng/checkbox';
import {FormsModule} from '@angular/forms';
import {Button} from 'primeng/button';
import {TranslocoDirective, TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {DirectusService, DirectusTranslationPipe, Module, ModuleFunction} from '@frontend/directus';
import {Divider} from 'primeng/divider';
import {ModuleFunctionControllerService} from '@frontend/shared-api';
import {firstValueFrom} from 'rxjs';
import {toSignal} from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-function-selection-dialog',
  imports: [
    Dialog,
    Checkbox,
    FormsModule,
    TranslocoDirective,
    Button,
    TranslocoPipe,
    Divider,
    DirectusTranslationPipe,
  ],
  templateUrl: './function-selection-dialog.component.html',
  styleUrl: './function-selection-dialog.component.scss',
})
export class FunctionSelectionDialogComponent implements OnInit, OnChanges {
  readonly visible = model(false);
  readonly label = input('');
  readonly initialSelectedFunctions = model<ModuleFunction[]>([]);
  readonly restrictToAvailableFunctions = input(false);
  readonly allowCrossModule = input(true);
  readonly readOnly = input(false);
  readonly translationPrefix = input.required<string>();
  readonly selectedFunctions = output<ModuleFunction[]>();

  readonly maxSelections = input<number | undefined>(undefined);
  readonly minSelections = input(0);

  private readonly directusService = inject(DirectusService);
  private readonly translocoService = inject(TranslocoService);
  private readonly moduleFunctionControllerService = inject(ModuleFunctionControllerService);

  protected readonly activeLanguage = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  protected readonly moduleFunctionsMap = signal<Map<number, Module>>(new Map());
  protected readonly selectedFunctionIds = signal<Set<number>>(new Set());
  protected readonly availableFunctionIds = signal<Set<number>>(new Set());
  private readonly availableFunctionsLoaded = signal(false);
  protected readonly isLoading = signal(false);
  private readonly firstSelectedModuleId = signal<number | null>(null);

  protected readonly isMaxSelectionsReached = computed(() => {
    const size = this.selectedFunctionIds().size;
    const max = this.maxSelections();
    return max !== undefined && size >= max;
  });

  protected readonly visibleModuleFunctionsEntries = computed(() => {
    return Array.from(this.moduleFunctionsMap().entries())
      .map(([key, value]) => {
        if (!this.restrictToAvailableFunctions()) {
          return {key, value};
        }

        const filteredFunctions = (value.functions || []).filter((func) =>
          this.availableFunctionIds().has(func.id),
        );
        return {
          key,
          value: {
            ...value,
            functions: filteredFunctions,
          },
        };
      })
      .filter((entry) => (entry.value.functions || []).length > 0);
  });

  protected readonly selectionLimitText = computed(() => {
    const max = this.maxSelections();
    if (!max) return '';
    return `${this.selectedFunctionIds().size}/${max}`;
  });

  protected readonly hasSelectionLimit = computed(() => {
    const max = this.maxSelections();
    return max !== undefined && max > 0;
  });

  protected readonly showCrossModuleWarning = computed(() => {
    return !this.allowCrossModule() && this.firstSelectedModuleId() !== null;
  });

  protected readonly isMinSelectionsNotMet = computed(() => {
    const min = this.minSelections();
    return min > 0 && this.selectedFunctionIds().size < min;
  });

  private readonly selectedModuleFunctions = computed(() => {
    const selectedIds = this.selectedFunctionIds();
    return Array.from(this.moduleFunctionsMap().values())
      .flatMap((module) => module.functions ?? [])
      .filter((func) => selectedIds.has(func.id));
  });

  protected readonly canConfirmSelection = computed(() => {
    const selectedIds = this.selectedFunctionIds();
    const selectedModuleFunctions = this.selectedModuleFunctions();
    const maxSelections = this.maxSelections();
    return (
      !this.readOnly() &&
      !this.isLoading() &&
      !this.isMinSelectionsNotMet() &&
      (maxSelections === undefined || selectedIds.size <= maxSelections) &&
      selectedModuleFunctions.length === selectedIds.size &&
      selectedModuleFunctions.every((func) => this.isFunctionAvailable(func)) &&
      (this.allowCrossModule() ||
        new Set(selectedModuleFunctions.map((func) => func.module_id.id)).size <= 1)
    );
  });

  ngOnInit(): void {
    this.loadModuleFunctions();
    if (this.restrictToAvailableFunctions()) {
      this.loadAvailableModuleFunctions();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.resetAndInitialize();
    }
  }

  public resetAndInitialize(): void {
    const newSet = new Set<number>();
    let firstModuleId: number | null = null;

    if (this.initialSelectedFunctions().length > 0) {
      for (const func of this.initialSelectedFunctions()) {
        if (func?.id && this.isKeptInSelection(func.id)) {
          newSet.add(func.id);
          if (!this.allowCrossModule() && firstModuleId === null) {
            firstModuleId = func.module_id.id;
          }
        }
      }
    }

    this.selectedFunctionIds.set(newSet);
    this.firstSelectedModuleId.set(firstModuleId);
  }

  private loadModuleFunctions(): void {
    this.isLoading.set(true);
    this.directusService.loadModuleFunctionsGroupedByModule().subscribe({
      next: (response: Map<number, Module>) => {
        this.moduleFunctionsMap.set(response);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      },
    });
  }

  private async loadAvailableModuleFunctions(): Promise<void> {
    try {
      const availableFunctions = await firstValueFrom(
        this.moduleFunctionControllerService.getAllModuleFunctions(),
      );
      const availableIds = new Set(availableFunctions.map((func) => Number(func.directusId)));
      this.availableFunctionIds.set(availableIds);
      this.availableFunctionsLoaded.set(true);
      this.selectedFunctionIds.update(
        (selectedIds) => new Set([...selectedIds].filter((id) => this.isKeptInSelection(id))),
      );
    } catch (error) {
      console.error('Error loading available module functions:', error);
    }
  }

  cancelSelection(): void {
    this.visible.set(false);
  }

  confirmSelection(): void {
    if (!this.canConfirmSelection()) {
      return;
    }
    this.selectedFunctions.emit(this.selectedModuleFunctions());
    this.visible.set(false);
  }

  toggleFunction(moduleFunction: ModuleFunction): void {
    if (this.isFunctionDisabled(moduleFunction)) {
      return;
    }

    const currentIds = this.selectedFunctionIds();
    const newSet = new Set(currentIds);

    if (currentIds.has(moduleFunction.id)) {
      newSet.delete(moduleFunction.id);
      this.selectedFunctionIds.set(newSet);

      if (!this.allowCrossModule() && newSet.size === 0) {
        this.firstSelectedModuleId.set(null);
      }
    } else {
      const maxSelections = this.maxSelections();
      if (maxSelections !== undefined && currentIds.size >= maxSelections) {
        return;
      }

      if (!this.allowCrossModule() && this.firstSelectedModuleId() === null) {
        this.firstSelectedModuleId.set(moduleFunction.module_id.id);
      }

      newSet.add(moduleFunction.id);
      this.selectedFunctionIds.set(newSet);
    }
  }

  protected isSelected(moduleFunction: ModuleFunction): boolean {
    return this.selectedFunctionIds().has(moduleFunction.id);
  }

  protected isFunctionAvailable(moduleFunction: ModuleFunction): boolean {
    if (!this.restrictToAvailableFunctions()) return true;
    return this.availableFunctionIds().has(moduleFunction.id);
  }

  private isKeptInSelection(functionId: number): boolean {
    return (
      !this.restrictToAvailableFunctions() ||
      !this.availableFunctionsLoaded() ||
      this.availableFunctionIds().has(functionId)
    );
  }

  protected isFunctionDisabled(moduleFunction: ModuleFunction): boolean {
    if (this.readOnly()) return true;
    if (!this.isFunctionAvailable(moduleFunction)) return true;
    if (this.isSelected(moduleFunction)) return false;
    if (this.isMaxSelectionsReached()) return true;

    const firstModuleId = this.firstSelectedModuleId();
    if (!this.allowCrossModule() && firstModuleId !== null) {
      return moduleFunction.module_id.id !== firstModuleId;
    }

    return false;
  }
}
